import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import LogRocket from "logrocket";
import { useSnackbar } from "notistack";
import copy from "copy-to-clipboard";
import { makeStyles } from "@material-ui/core/styles";
import RecordingShareToast from "../../components/RecordingShareToast/RecordingShareToast";
import {
  ensureRecordingReady,
  getSessionURL,
  MAX_RECORDING_MS,
  startNewRecordingSession,
  WARNING_THRESHOLD_MS,
} from "../recordingSession";

export type RecordingIdentity = {
  sub: string;
  email?: string;
};

type RecordingContextValue = {
  isRecording: boolean;
  isPaused: boolean;
  startedAt: Date | null;
  sessionURL: string | null;
  isIndicatorVisible: boolean;
  previousRecordings: RecordingHistoryEntry[];
  start: () => void;
  stopAndShare: () => void;
  pause: () => void;
  resume: () => void;
  showIndicator: () => void;
  hideIndicator: () => void;
};

const RecordingContext = createContext<RecordingContextValue | undefined>(undefined);

const STORAGE_KEY = "recordingIndicator:state";
const HISTORY_KEY = "recordingIndicator:history";

type StoredState = {
  startedAtMs: number;
  sessionURL: string | null;
};

export type RecordingHistoryEntry = {
  url: string;
  timestamp: number;
  durationMs: number;
};

const readHistory = (): RecordingHistoryEntry[] => {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (e) => typeof e?.url === "string" && typeof e?.timestamp === "number" && typeof e?.durationMs === "number"
    );
  } catch {
    return [];
  }
};

const addToHistory = (entry: RecordingHistoryEntry): RecordingHistoryEntry[] => {
  const updated = [entry, ...readHistory()];
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  } catch {
    // ignore quota / disabled storage
  }
  return updated;
};

const readStoredState = (): StoredState | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed?.startedAtMs !== "number") {
      return null;
    }
    return {
      startedAtMs: parsed.startedAtMs,
      sessionURL: typeof parsed.sessionURL === "string" ? parsed.sessionURL : null,
    };
  } catch {
    return null;
  }
};

const writeStoredState = (state: StoredState) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / disabled storage
  }
};

const clearStoredState = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export class RecordingErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error("[RecordingErrorBoundary] caught error in recording feature:", error);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

const useStyles = makeStyles((theme) => ({
  toastContainer: {
    position: "fixed",
    bottom: theme.spacing(3),
    left: theme.spacing(3),
    zIndex: theme.zIndex.snackbar,
  },
}));

export const RecordingProvider = ({
  children,
  getIdentity,
}: {
  children: React.ReactNode;
  getIdentity?: () => Promise<RecordingIdentity | undefined> | undefined;
}) => {
  const classes = useStyles();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const [sessionURL, setSessionURL] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isIndicatorVisible, setIsIndicatorVisible] = useState(false);
  const [previousRecordings, setPreviousRecordings] = useState<RecordingHistoryEntry[]>(() => readHistory());
  const { enqueueSnackbar } = useSnackbar();
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captureTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const sessionURLRef = useRef<string | null>(null);
  const startedAtMsRef = useRef<number | null>(null);
  const hasRestoredRef = useRef(false);

  // Keep a ref in sync with the latest URL so the captureUrl poll can read-modify-write
  // localStorage without re-creating the start() callback on every render.
  useEffect(() => {
    sessionURLRef.current = sessionURL;
  }, [sessionURL]);

  useEffect(() => {
    startedAtMsRef.current = startedAt ? startedAt.getTime() : null;
  }, [startedAt]);

  const showIndicator = useCallback(() => setIsIndicatorVisible(true), []);
  const hideIndicator = useCallback(() => setIsIndicatorVisible(false), []);

  const clearTimers = useCallback(() => {
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
    }
    if (warnTimerRef.current) {
      clearTimeout(warnTimerRef.current);
    }
    captureTimersRef.current.forEach(clearTimeout);
    captureTimersRef.current = [];
    maxTimerRef.current = null;
    warnTimerRef.current = null;
  }, []);

  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);

  const stopAndShare = useCallback(() => {
    // Prefer the URL captured at recording start (persisted across reloads).
    // Fall back to the LogRocket SDK's current URL if we never captured one.
    const url = sessionURLRef.current ?? getSessionURL();
    if (url) {
      copy(url);
      setShareUrl(url);
      const durationMs = startedAtMsRef.current ? Date.now() - startedAtMsRef.current : 0;
      const updated = addToHistory({ url, timestamp: Date.now(), durationMs });
      setPreviousRecordings(updated);
    } else {
      enqueueSnackbar("Recording stopped — session link not yet available", { variant: "warning" });
    }
    clearStoredState();
    clearTimers();
    setIsRecording(false);
    setIsPaused(false);
    setStartedAt(null);
    setSessionURL(null);
  }, [enqueueSnackbar, clearTimers]);

  // Ref kept in sync so the auto-stop timer always calls the latest stopAndShare,
  // avoiding a stale closure if enqueueSnackbar identity changes between schedule and fire.
  const stopAndShareRef = useRef(stopAndShare);
  useEffect(() => {
    stopAndShareRef.current = stopAndShare;
  }, [stopAndShare]);

  const captureAndPersistUrl = useCallback(() => {
    if (sessionURLRef.current) {
      return;
    }
    const url = getSessionURL();
    if (!url) {
      return;
    }
    setSessionURL(url);
    const stored = readStoredState();
    if (stored) {
      writeStoredState({ ...stored, sessionURL: url });
    }
  }, []);

  const scheduleTimers = useCallback(
    (startedAtMs: number) => {
      const elapsed = Date.now() - startedAtMs;
      const warnIn = Math.max(0, WARNING_THRESHOLD_MS - elapsed);
      const maxIn = Math.max(0, MAX_RECORDING_MS - elapsed);
      warnTimerRef.current = setTimeout(() => {
        enqueueSnackbar("Recording will stop automatically in 2 minutes", { variant: "warning" });
      }, warnIn);
      maxTimerRef.current = setTimeout(() => {
        stopAndShareRef.current();
      }, maxIn);
    },
    [enqueueSnackbar] // stopAndShare read via ref — stable, no dep needed
  );

  const start = useCallback(() => {
    const ok = ensureRecordingReady();
    if (!ok) {
      enqueueSnackbar("Recording is not available — LogRocket app id is not configured", { variant: "error" });
      return;
    }
    // Identify the user for audit trail before starting the new session.
    // Fire-and-forget: a slow/failing getIdentity() must not block the recording.
    getIdentity?.()
      ?.then((identity) => {
        if (identity?.sub) {
          LogRocket.identify(identity.sub, {
            email: identity.email ?? "",
          });
        }
      })
      ?.catch(() => {});
    startNewRecordingSession();
    const startedAtMs = Date.now();
    setIsRecording(true);
    setStartedAt(new Date(startedAtMs));
    setSessionURL(null);
    sessionURLRef.current = null;
    writeStoredState({ startedAtMs, sessionURL: null });
    captureTimersRef.current.push(setTimeout(captureAndPersistUrl, 1500));
    captureTimersRef.current.push(setTimeout(captureAndPersistUrl, 4000));
    scheduleTimers(startedAtMs);
  }, [enqueueSnackbar, captureAndPersistUrl, scheduleTimers, getIdentity]);

  // Restore in-progress recording across page reloads / full-page navigations.
  // hasRestoredRef guards against repeating the restore if scheduleTimers' identity
  // ever changes (e.g. HMR or unrelated provider re-render).
  useEffect(() => {
    if (hasRestoredRef.current) {
      return;
    }
    hasRestoredRef.current = true;
    const stored = readStoredState();
    if (!stored) {
      return;
    }
    // Re-init LogRocket so the post-reload portion is still captured under the user.
    // We do NOT call startNewRecordingSession on restore — we want LogRocket to keep
    // running from page load. We also do NOT overwrite the saved sessionURL with the
    // post-reload one; stopAndShare will copy the original URL.
    ensureRecordingReady();
    setIsRecording(true);
    setStartedAt(new Date(stored.startedAtMs));
    setSessionURL(stored.sessionURL);
    sessionURLRef.current = stored.sessionURL;
    setIsIndicatorVisible(true);
    scheduleTimers(stored.startedAtMs);
  }, [scheduleTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <RecordingContext.Provider
      value={{
        isRecording,
        isPaused,
        startedAt,
        sessionURL,
        isIndicatorVisible,
        previousRecordings,
        start,
        stopAndShare,
        pause,
        resume,
        showIndicator,
        hideIndicator,
      }}
    >
      {children}
      {shareUrl !== null && (
        <div className={classes.toastContainer}>
          <RecordingShareToast url={shareUrl} onDismiss={() => setShareUrl(null)} />
        </div>
      )}
    </RecordingContext.Provider>
  );
};

export const useRecording = (): RecordingContextValue => {
  const ctx = useContext(RecordingContext);
  if (ctx === undefined) {
    throw new Error("useRecording must be used within a RecordingProvider");
  }
  return ctx;
};

/**
 * For components that can legitimately render without a RecordingProvider above them - the app header
 * still renders inside RecordingErrorBoundary's fallback, which drops the provider - and should just
 * leave the recording controls out rather than throw on every render.
 */
export const useRecordingIfAvailable = (): RecordingContextValue | undefined => useContext(RecordingContext);
